import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, filter, map, takeUntil, withLatestFrom, combineLatest } from 'rxjs';
import { selectIsIra } from '../../../account-info-header/reducers';
import { buildFromPrefilledForm } from '../../../create-request/utilities/formUtilities';
import { InfoToAccount } from '../../../shared/models/InfoToAccount';
import { ComponentState } from '../../../shared/types/ComponentState';
import { Constants, RegTypeCode } from '../../../shared/utilities/Constants';
import { StandingAuthFormActions, StandingAuthReviewPageActions } from '../../actions';
import { ItStandingAuthDetails } from '../../models/standingAuthorizationForm';
import {
  selectStandingAuthInternalTransferInfo,
  selectInternalTransferInputCallFinished,
  selectIsFromEditInProgress,
  selectOpenTwePopupConditions,
  selectToAccountInfoErrorText,
  selectToAccountRegistrationType,
  selectToAccountState,
} from '../../reducers';

@Component({
  standalone: false,
  selector: 'mm-standing-authorization-details-internal-transfers',
  templateUrl: './sa-details-internal-transfers.component.html',
  styleUrls: ['./sa-details-internal-transfers.component.scss'],
})
export class StandingAuthorizationDetailsInternalTransfersComponent implements OnInit, OnDestroy {
  @Input() parentForm: FormGroup;
  @Input() prefilledPromptsObs: Observable<ItStandingAuthDetails>;
  standingAuthorizationDetailsGroup: FormGroup;
  info$: Observable<InfoToAccount>;
  toAccountState$: Observable<ComponentState>;
  destroy$ = new Subject<void>();
  serverError: boolean;
  serverErrorText: string;
  registrationType: string;
  recipentError: string = Constants.recipentError;
  toAccountValidationError: string = Constants.toAccountValidationError;
  isIra$: Observable<boolean>;
  inputCallFinished$: Observable<boolean>;
  is401k: boolean = false;
  warningText401kIneligableForEauth: string = Constants.accountTypeIneligibleForEauth;

  constructor(private fb: FormBuilder, private store: Store) { }

  ngOnInit(): void {
    this.standingAuthorizationDetailsGroup = this.fb.group({});
    this.parentForm.setControl(
      'standingAuthorizationDetails',
      this.standingAuthorizationDetailsGroup,
      { emitEvent: false }
    );

    this.info$ = this.store.select(selectStandingAuthInternalTransferInfo).pipe(
      map((val) => {
        let info: InfoToAccount = {
          inputCallFinished: val.inputCallFinished,
          toAccountInfo: val.toAccountInfo,
        };
        return info;
      })
    );

    this.toAccountState$ = this.store.select(selectToAccountState);
    this.isIra$ = this.store.select(selectIsIra);
    this.inputCallFinished$ = this.store.select(selectInternalTransferInputCallFinished);

    this.store.select(selectToAccountRegistrationType).pipe(
      takeUntil(this.destroy$)
    ).subscribe((regType) => {
      this.registrationType = regType;
      this.is401k = regType === RegTypeCode.INDIVIDUAL_401K || regType === RegTypeCode.ROTH_401K;
    });

    this.store.select(selectToAccountInfoErrorText).pipe(
      takeUntil(this.destroy$)
    ).subscribe((errorText) => {
      if (errorText) {
        this.serverError = true;
        this.serverErrorText = errorText;
        this.standingAuthorizationDetailsGroup.controls.toAccount.setValue('');
      } else {
        this.serverError = false;
        this.serverErrorText = '';
      }
    });

    this.store.select(selectOpenTwePopupConditions).pipe(
      takeUntil(this.destroy$)
    ).subscribe((tweConditions) => {
      const { verifyCallFinished, openTwePopup } = tweConditions;
      if (verifyCallFinished && !openTwePopup)
        this.store.dispatch(StandingAuthFormActions.loadReviewPage());
    });

    this.prefilledPromptsObs.pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.store.select(selectIsFromEditInProgress)),
      filter(([prefilledPrompts, isFromEdit]) => !!prefilledPrompts && isFromEdit)
    ).subscribe(([prefilledPrompts, _isFromEdit]) => {
      buildFromPrefilledForm(
        prefilledPrompts,
        this.standingAuthorizationDetailsGroup
      );
      this.store.dispatch(StandingAuthReviewPageActions.editCompleted());
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isInvalidDigitalToPdfToAccount(): boolean {
    return this.registrationType === RegTypeCode.CUSTODIAL;
  }

  get showServerError() {
    const isCustodialToAccount = this.registrationType === RegTypeCode.CUSTODIAL;
    if (this.is401k) {
      return this.serverError && this.serverErrorText != this.recipentError;
    }
    else if (isCustodialToAccount) {
      return this.serverError && this.serverErrorText != this.recipentError;
    }
    else {
      return this.serverError && !this.isInvalidDigitalToPdfToAccount;
    }
  }
}
